// v1.2.0 — the one way the app turns a secret into a key.
//
//   const d = await derive(secret, salt);   // ~0.5 s on a phone
//   d.key      the private key (memory only — never store it, never send it)
//   d.address  the controller this secret stands for
//   d.ms       how long the KDF took — write it to Lab as
//              `notes/kdf: ${d.ms} ms (64 MiB, wasm, ${d.where})`
//   d.where    "worker" (the normal case) or "inline" (see below)
//
// The KDF (argon2id, 64 MiB — lockcode.ts) runs in a Web Worker that is
// started for the job and terminated after it, so the slider and the code
// boxes never stutter and the memory is given back. lockcode.ts and hash-wasm
// are reachable ONLY through the worker file and the dynamic import below, so
// they are never in the main bundle.
//
// Fallback: where a Worker can't be started (no Worker global — Node, tests —
// or the browser refuses to construct one), the same derivation runs on the
// calling thread through a dynamic import. It still works; it may stutter;
// `where` says which one ran.

import type { Hex } from "viem";
import { privateKeyToAccount } from "viem/accounts";

export interface Derived {
  key: Hex;
  address: Hex;
  ms: number;
  where: "worker" | "inline";
}

type Reply = { ok: true; key: Hex } | { ok: false; error: string };

const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

function inWorker(secret: string, salt: Hex): Promise<Hex> {
  return new Promise<Hex>((resolve, reject) => {
    const w = new Worker(new URL("./kdf.worker.ts", import.meta.url), { type: "module" });
    const done = (fn: () => void) => { w.terminate(); fn(); };
    w.onmessage = (e: MessageEvent<Reply>) =>
      done(() => (e.data.ok ? resolve(e.data.key) : reject(new Error(e.data.error))));
    w.onerror = (e) => done(() => reject(new Error(e.message || "kdf worker failed")));
    w.postMessage({ secret, salt });
  });
}

async function inline(secret: string, salt: Hex): Promise<Hex> {
  const { keyFromSecret } = await import("./lockcode");
  return keyFromSecret(secret, salt);
}

/**
 * KDF v1: the key and controller address a secret stands for, given the note's
 * salt. `secret` is the 11 data symbols of a code (secretFromCode) or the 16
 * symbols of a link fragment — never the 12-symbol code with its check.
 */
export async function derive(secret: string, salt: Hex): Promise<Derived> {
  const t0 = now();
  let key: Hex;
  let where: Derived["where"] = "worker";
  if (typeof Worker === "undefined") {
    where = "inline";
    key = await inline(secret, salt);
  } else {
    try {
      key = await inWorker(secret, salt);
    } catch {
      // The worker could not start or died (blocked, out of memory): once more, here.
      where = "inline";
      key = await inline(secret, salt);
    }
  }
  return { key, address: privateKeyToAccount(key).address, ms: Math.round(now() - t0), where };
}
